import { Divider, Flex, UsersIcon } from 'components'
import { useContext, useEffect, useRef, useState } from 'react'
import Video from 'react-player'
import { MediaWithUrl } from 'types'
import { useWindowSize } from 'usehooks-ts'
import AnimatedHand from '../components/AnimatedHand'
import { SessionContext, useExplorerConfig } from '../components/providers'
import { SIconCircle, SText, SView } from '../components/scaled'
import { useDefaultBackground } from '../utils/traitUtils'
/** (ms) time after screensaver starts to show the video */
const startVideoDelay = 5 * 1000

export default function ScreensaverView() {
  const { width = 0, height = 0 } = useWindowSize()
  const { status } = useContext(SessionContext)
  const { facility } = useExplorerConfig()
  const [show, setShow] = useState(false)

  const { companyName, visitorNames } = facility || {}
  const { title, coverImage, mediaItems, subtitle, helpText } = useScreensaver()
  const defaultBackground = useDefaultBackground()

  const timeoutRef = useRef<Timer>(undefined)
  useEffect(() => {
    clearTimeout(timeoutRef.current)
    if (status !== 'screensaver') return

    timeoutRef.current = setTimeout(() => setShow(true), startVideoDelay)
  }, [status])

  // TODO - handle multiple items with possible images and looping
  const { url, dimensions } = mediaItems?.[0] || {}
  const videoRatio = (dimensions?.width ?? 0) / (dimensions?.height ?? 0)
  const screenRatio = width / height
  const videoWidth = screenRatio > videoRatio ? height * videoRatio : width
  const videoHeight = screenRatio > videoRatio ? height : width / videoRatio

  return (
    <Flex
      flex={1}
      bgImage={`url(${coverImage?.url || defaultBackground})`}
      backgroundSize="cover"
      backgroundRepeat="no-repeat"
      backgroundPosition="center center"
      position="relative"
    >
      {show && url && (
        <Flex position="absolute" inset={0}>
          <Flex
            css={{ flex: 1, backgroundColor: 'black', alignItems: 'center', overflow: 'hidden' }}
          >
            <Video
              width={videoWidth}
              height={videoHeight}
              style={{ flex: 1 }}
              loop
              playing
              volume={0}
              src={url}
            />
          </Flex>
          <Flex position="absolute" inset={0} css={{ backgroundColor: 'rgba(0,0,0,0.3)' }} />
        </Flex>
      )}

      <SView
        position="absolute"
        bottom={facility ? 80 : 300}
        left={80}
        gap={facility ? 20 : 80}
        maxW={facility ? '80%' : '45%'}
      >
        <SText fontWeight="bold" fontSize={facility ? 50 : 70}>
          {companyName ?? title}
        </SText>
        {Boolean(visitorNames?.length) ? (
          <SView direction="row" alignItems="center" gap={18}>
            <SIconCircle Icon={UsersIcon} />
            <SText fontWeight="bold" fontSize={30}>
              {visitorNames?.join(', ')}
            </SText>
          </SView>
        ) : subtitle ? (
          <SText fontWeight="bold" fontSize={30}>
            {subtitle}
          </SText>
        ) : null}
        {!facility && (
          <>
            <Divider />
            <SView direction="row" gap={30}>
              <SText fontWeight="bold" fontSize={30}>
                {helpText}
              </SText>
              <AnimatedHand />
            </SView>
          </>
        )}
      </SView>
    </Flex>
  )
}

function useScreensaver() {
  const { templateDocs, workspace } = useExplorerConfig()
  const { getLanguageValue } = useContext(SessionContext)
  const { media = [], template } = templateDocs || {}
  const mediaById = media.reduce(
    (acc, cur) => ({ ...acc, [cur.id]: cur }),
    {} as Record<string, MediaWithUrl>
  )
  const { helpText, initialImage, items, subtitle, title } = template?.screensaverMedia || {}
  const registrationTitle = getLanguageValue(template?.registration?.title)

  return {
    coverImage: initialImage ? mediaById[initialImage] : undefined,
    helpText: getLanguageValue(helpText) ?? 'Touch the screen to begin your journey.',
    mediaItems: items?.map(i => mediaById[i.id]),
    subtitle: getLanguageValue(subtitle),
    title: getLanguageValue(title) ?? registrationTitle ?? workspace?.title,
  }
}
